import { supabase } from "@/integrations/supabase/client";

export interface DailyRecap {
  id: string;
  date: string;
  script: string;
  audio_url: string | null;
  article_count: number | null;
  article_ids: string[] | null;
  created_at: string | null;
  is_published: boolean | null;
  published_at: string | null;
  video_url?: string | null;
  video_urls?: unknown;
  video_status?: string | null;
  video_error?: string | null;
}

export interface ListenStatus {
  started: boolean;
  completed: boolean;
  xp_awarded: boolean;
}

export const dailyRecapService = {
  /** Admin only: kicks off video generation for a recap. */
  async startRecapVideo(recapId: string): Promise<void> {
    const { data, error } = await supabase.functions.invoke('generate-recap-video', {
      body: { recapId },
    });
    if (error) throw error;
    if (data?.error) throw new Error(data.error);
  },

  /** Admin only: current video state for a recap. */
  async getRecapVideoState(recapId: string) {
    const { data, error } = await supabase
      .from('daily_recaps')
      .select('id, video_url, video_urls, video_status, video_error')
      .eq('id', recapId)
      .maybeSingle();
    if (error) throw error;
    return data;
  },

  async getLatestRecap(): Promise<DailyRecap | null> {
    const { data, error } = await supabase
      .from('daily_recaps')
      .select('*')
      .order('date', { ascending: false })
      .limit(1)
      .maybeSingle();

    if (error) {
      console.error('Error fetching latest recap:', error);
      return null;
    }

    return data;
  },

  async getTodayRecap(): Promise<DailyRecap | null> {
    const today = new Date().toISOString().split('T')[0];
    
    const { data, error } = await supabase
      .from('daily_recaps')
      .select('*')
      .eq('date', today)
      .maybeSingle();

    if (error) {
      console.error('Error fetching today recap:', error);
      return null;
    }

    return data;
  },

  /**
   * Player queue: the script column is the bulk of a recap row (~28 KB for the
   * full list), and the player never renders it, so it is left out here.
   */
  async getAllPublishedRecaps(): Promise<DailyRecap[]> {
    const { data, error } = await supabase
      .from('daily_recaps')
      .select('id, date, audio_url, article_count, article_ids, created_at, is_published, published_at')
      .eq('is_published', true)
      .order('date', { ascending: false })
      .limit(30);

    if (error) {
      console.error('Error fetching all recaps:', error);
      return [];
    }

    return (data || []).map((row) => ({ ...row, script: '' })) as DailyRecap[];
  },

  async getUserRecapStatus(userId: string, recapId: string): Promise<ListenStatus> {
    const { data } = await supabase
      .from('user_daily_recap_listens')
      .select('*')
      .eq('user_id', userId)
      .eq('recap_id', recapId)
      .maybeSingle();

    if (!data) {
      return {
        started: false,
        completed: false,
        xp_awarded: false,
      };
    }

    return {
      started: true,
      completed: !!data.completed_at,
      xp_awarded: data.xp_awarded ?? false,
    };
  },

  async markRecapStarted(recapId: string): Promise<void> {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) return;

    // Check if already exists
    const { data: existing } = await supabase
      .from('user_daily_recap_listens')
      .select('id')
      .eq('user_id', user.id)
      .eq('recap_id', recapId)
      .maybeSingle();

    if (existing) return;

    await supabase
      .from('user_daily_recap_listens')
      .insert({
        user_id: user.id,
        recap_id: recapId,
      });
  },

  async markRecapCompleted(recapId: string): Promise<{ xp_earned: number }> {
    const { data, error } = await supabase.functions.invoke('complete-daily-recap', {
      body: { recap_id: recapId },
    });

    if (error) {
      console.error('Error completing recap:', error);
      throw error;
    }

    return data;
  },

  async generateDailyRecap(force: boolean = false): Promise<DailyRecap> {
    const { data, error } = await supabase.functions.invoke('generate-daily-recap', {
      body: { force }
    });

    if (error) {
      console.error('Error generating recap:', error);
      const context = (error as { context?: Response }).context;
      if (context && typeof context.json === 'function') {
        const payload = await context.json().catch(() => null);
        throw new Error(payload?.error || error.message);
      }
      throw error;
    }

    if (data.error) {
      throw new Error(data.error);
    }

    return data.recap;
  },

  async getRecapArticles(articleIds: string[]) {
    const { data, error } = await supabase
      .from('articles')
      .select('id, title, source_name, source_logo_url, published_at, url')
      .in('id', articleIds)
      .order('published_at', { ascending: false });
    
    if (error) throw error;
    return data || [];
  },

  async publishRecap(recapId: string): Promise<DailyRecap> {
    const { data, error } = await supabase.functions.invoke('publish-daily-recap', {
      body: { recap_id: recapId, is_published: true },
    });

    if (error) {
      console.error('Error publishing recap:', error);
      const context = (error as { context?: Response }).context;
      if (context && typeof context.json === 'function') {
        const payload = await context.json().catch(() => null);
        throw new Error(payload?.error || error.message);
      }
      throw error;
    }

    if (data.error) {
      throw new Error(data.error);
    }

    return data.recap;
  },

  async unpublishRecap(recapId: string): Promise<DailyRecap> {
    const { data, error } = await supabase.functions.invoke('publish-daily-recap', {
      body: { recap_id: recapId, is_published: false },
    });

    if (error) {
      console.error('Error unpublishing recap:', error);
      throw error;
    }

    if (data.error) {
      throw new Error(data.error);
    }

    return data.recap;
  },
};
